import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Send, Brain, RefreshCw, Play, CheckCircle, AlertCircle, Zap, FlaskConical, Lightbulb, ExternalLink, Mic, MicOff, Volume2, VolumeX } from 'lucide-react';
import { FileAttachments } from '../components/FileAttachments';
import { useVoice } from '../hooks/useVoice';

const API = process.env.REACT_APP_BACKEND_URL;
const CONV_ID = 'einstein-main';

const GREETING = {
  type: 'message',
  message: "Hey, I'm Einstein — your IdeaWorks Content Strategist. I have full control over the platform: topic engines, blog publishing, content calendars, competitor analysis, and more.\n\nWhat would you like to achieve today?",
  suggestions: [
    'Generate a week of content for qlia.io',
    'Analyze competitors and find content gaps',
    'Set up content calendar with 3-day advance drafting',
    'Create click-optimized social posts from my latest blog',
    'Run the autoresearch performance loop',
  ],
};

export default function ContentStrategist() {
  const [input, setInput] = useState('');
  const [messages, setMessages] = useState([{ role: 'assistant', content: GREETING, ts: Date.now() }]);
  const [loading, setLoading] = useState(false);
  const [executingId, setExecutingId] = useState(null);
  const [execResults, setExecResults] = useState({});
  const [attachments, setAttachments] = useState([]);
  const endRef = useRef(null);
  const inputRef = useRef(null);
  const navigate = useNavigate();
  const isFirstUserMessage = messages.filter(m => m.role === 'user').length === 0;
  const { voiceEnabled, setVoiceEnabled, isListening, isSpeaking, speak, startListening } = useVoice();

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const sendMessage = async (text) => {
    let msg = (text || input).trim();
    if (!msg || loading) return;

    // If this is the first user message, inject discovery instruction
    if (isFirstUserMessage && !text) {
      msg = `[DISCOVERY MODE] Before starting, conduct a smart discovery session: ask me focused questions one at a time (up to 6 max) to understand my goals, audience, constraints, and success criteria. Start with your first question now.\n\nMy request: ${msg}`;
    }

    setInput('');
    const sentAttachments = [...attachments];
    setAttachments([]);

    setMessages(prev => [...prev, {
      role: 'user',
      content: msg,
      attachments: sentAttachments.map(a => a.name),
      ts: Date.now(),
    }]);
    setLoading(true);

    try {
      const { data } = await axios.post(`${API}/api/strategist/chat`, {
        message: msg,
        conversation_id: CONV_ID,
        attachments: sentAttachments.map(a => ({ name: a.name, type: a.type, content: a.content })),
      });
      setMessages(prev => [...prev, { role: 'assistant', content: data.response, ts: Date.now() }]);
      // Read response aloud if voice enabled
      const responseText = typeof data.response === 'string' ? data.response : (data.response?.message || '');
      speak(responseText.slice(0, 400));
    } catch {
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: { type: 'error', message: 'Einstein encountered an error. Please check the backend connection.' },
        ts: Date.now(),
      }]);
    } finally {
      setLoading(false);
    }
  };

  const executeAction = async (action) => {
    setExecutingId(action.id);
    try {
      const method = (action.method || 'POST').toLowerCase();
      const res = method === 'get'
        ? await axios.get(`${API}${action.endpoint}`)
        : await axios.post(`${API}${action.endpoint}`, action.body);
      setExecResults(prev => ({ ...prev, [action.id]: `Done: ${JSON.stringify(res.data).slice(0, 120)}...` }));
      await sendMessage(`I just executed: "${action.description}". What should I do next?`);
    } catch (err) {
      setExecResults(prev => ({ ...prev, [action.id]: `Error: ${String(err).slice(0, 100)}` }));
    } finally {
      setExecutingId(null);
    }
  };

  const reset = async () => {
    try { await axios.delete(`${API}/api/strategist/reset/${CONV_ID}`); } catch { }
    setMessages([{ role: 'assistant', content: GREETING, ts: Date.now() }]);
    setExecResults({});
  };

  const parseContent = (content) => {
    if (typeof content === 'string') return { type: 'message', message: content };
    return content;
  };

  return (
    <div className="h-full flex flex-col" style={{ background: 'var(--llmc-bg)' }}>
      {/* Header */}
      <div className="flex items-center gap-3 px-4 py-3 flex-shrink-0"
        style={{ background: 'rgba(10,10,20,0.95)', borderBottom: '1px solid var(--llmc-border)' }}>
        <div className="w-10 h-10 rounded-xl flex items-center justify-center"
          style={{ background: 'linear-gradient(135deg, #7c5efa, #00d4b8)' }}>
          <Brain size={20} className="text-white" />
        </div>
        <div className="flex-1">
          <div className="font-black text-white text-sm" style={{ letterSpacing: '-0.01em' }}>Einstein</div>
          <div className="text-xs" style={{ color: 'var(--llmc-text-muted)' }}>AI Content Strategist — Full Platform Control</div>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="text-[10px] font-bold px-2 py-1 rounded-full"
            style={{ background: 'rgba(124,94,250,0.12)', border: '1px solid rgba(124,94,250,0.3)', color: '#a68eff' }}>
            Claude Sonnet 4.6
          </span>
          <button onClick={reset} data-testid="einstein-reset-btn"
            className="p-1.5 rounded-lg transition-opacity hover:opacity-80" style={{ opacity: 0.5, color: 'var(--llmc-text-muted)' }}>
            <RefreshCw size={14} />
          </button>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 flex flex-col gap-3">
        {messages.map((msg, i) => {
          const resp = parseContent(msg.content);
          const isUser = msg.role === 'user';
          return (
            <div key={i} className="flex gap-3 fade-in" style={{ justifyContent: isUser ? 'flex-end' : 'flex-start' }}>
              {!isUser && (
                <div className="w-8 h-8 rounded-lg flex-shrink-0 mt-0.5 flex items-center justify-center"
                  style={{ background: 'linear-gradient(135deg, #7c5efa, #00d4b8)' }}>
                  <Zap size={14} className="text-white" />
                </div>
              )}
              <div className="flex flex-col gap-1.5" style={{ maxWidth: '75%' }}>
                {/* Message bubble */}
                <div className="px-4 py-3 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap"
                  style={{
                    background: isUser ? 'linear-gradient(135deg, rgba(124,94,250,0.2), rgba(0,212,184,0.1))' : 'rgba(255,255,255,0.04)',
                    border: isUser ? '1px solid rgba(124,94,250,0.3)' : '1px solid rgba(255,255,255,0.06)',
                    borderRadius: isUser ? '16px 4px 16px 16px' : '4px 16px 16px 16px',
                    color: 'var(--llmc-text)',
                  }}>
                  {typeof msg.content === 'string' ? msg.content : resp.message}
                  {/* Show attached file names */}
                  {isUser && msg.attachments?.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-2 pt-2" style={{ borderTop: '1px solid rgba(255,255,255,0.1)' }}>
                      {msg.attachments.map((name, ai) => (
                        <span key={ai} className="text-[10px] px-1.5 py-0.5 rounded" style={{ background: 'rgba(124,94,250,0.2)', color: '#a68eff' }}>
                          {name}
                        </span>
                      ))}
                    </div>
                  )}
                </div>

                {/* Clarifying Questions */}
                {resp.type === 'questions' && resp.questions?.map((q, qi) => (
                  <div key={qi} className="px-3 py-2 rounded-lg text-sm" data-testid={`question-${qi}`}
                    style={{ background: 'rgba(124,94,250,0.06)', border: '1px solid rgba(124,94,250,0.2)', color: '#a68eff', fontWeight: 500 }}>
                    {qi + 1}. {q}
                  </div>
                ))}

                {/* Action Plan */}
                {resp.actions?.length > 0 && (
                  <div className="flex flex-col gap-1.5">
                    <span className="text-xs px-1" style={{ color: 'var(--llmc-text-muted)' }}>Action Plan:</span>
                    {resp.actions.map(action => {
                      const isDone = !!execResults[action.id];
                      const isRunning = executingId === action.id;
                      return (
                        <div key={action.id} className="flex items-center gap-3 px-3 py-2.5 rounded-xl"
                          data-testid={`action-${action.id}`}
                          style={{
                            background: isDone ? 'rgba(61,255,160,0.06)' : 'rgba(255,255,255,0.03)',
                            border: `1px solid ${isDone ? 'rgba(61,255,160,0.3)' : 'rgba(255,255,255,0.08)'}`,
                          }}>
                          <div className="flex-1 min-w-0">
                            <div className="text-xs font-semibold text-white truncate">{action.label || action.description}</div>
                            <div className="text-[10px]" style={{ color: 'var(--llmc-text-muted)' }}>{action.method} {action.endpoint}</div>
                            {isDone && <div className="text-[10px] mt-0.5" style={{ color: '#3dffa0' }}>{execResults[action.id]}</div>}
                          </div>
                          {isDone
                            ? <CheckCircle size={18} style={{ color: '#3dffa0', flexShrink: 0 }} />
                            : (
                              <button onClick={() => executeAction(action)} disabled={isRunning}
                                className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 transition-all"
                                style={{ background: 'rgba(124,94,250,0.15)', border: '1px solid rgba(124,94,250,0.3)' }}>
                                {isRunning
                                  ? <div className="w-3 h-3 rounded-full border-2 border-t-transparent animate-spin" style={{ borderColor: '#7c5efa', borderTopColor: 'transparent' }} />
                                  : <Play size={12} style={{ color: '#a68eff' }} />}
                              </button>
                            )}
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Error */}
                {resp.type === 'error' && (
                  <div className="flex items-center gap-2 px-3 py-2 rounded-lg text-xs"
                    style={{ background: 'rgba(235,87,87,0.1)', border: '1px solid rgba(235,87,87,0.3)', color: '#eb5757' }}>
                    <AlertCircle size={14} />
                    {resp.error === 'NO_API_KEY' ? 'Convex LLM keys not loaded. Ensure ANTHROPIC_API_KEY is set.' : 'Service error'}
                  </div>
                )}

                {/* Brainstorm Route */}
                {resp.type === 'brainstorm' && (
                  <div className="rounded-xl p-3" style={{ background: 'rgba(255,83,112,0.08)', border: '1px solid rgba(255,83,112,0.3)' }}>
                    <div className="text-xs font-bold text-white mb-2 flex items-center gap-1.5">
                      <Lightbulb size={13} style={{ color: '#ff7a94' }} />
                      Brainstorm Lab Recommended
                    </div>
                    <div className="text-xs mb-3" style={{ color: 'var(--llmc-text-muted)' }}>
                      4-round AI council (GPT-4o · Gemini · Perplexity · Groq + Claude judge) will explore this idea.
                    </div>
                    <button
                      onClick={() => navigate('/brainstorm')}
                      data-testid="einstein-go-brainstorm-btn"
                      className="flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-bold transition-all hover:scale-105"
                      style={{ background: 'linear-gradient(135deg, #ff5370, #7c5efa)', color: '#fff' }}>
                      <Lightbulb size={13} /> Open Brainstorm Lab <ExternalLink size={11} />
                    </button>
                  </div>
                )}

                {/* Research Route */}
                {resp.type === 'research' && (
                  <div className="rounded-xl p-3" style={{ background: 'rgba(61,255,160,0.06)', border: '1px solid rgba(61,255,160,0.25)' }}>
                    <div className="text-xs font-bold text-white mb-2 flex items-center gap-1.5">
                      <FlaskConical size={13} style={{ color: '#3dffa0' }} />
                      Research Lab Recommended
                    </div>
                    <div className="text-xs mb-3" style={{ color: 'var(--llmc-text-muted)' }}>
                      8-phase autonomous research: literature discovery (Semantic Scholar + arXiv) → synthesis → draft → peer review.
                    </div>
                    <button
                      onClick={() => navigate('/research')}
                      data-testid="einstein-go-research-btn"
                      className="flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-bold transition-all hover:scale-105"
                      style={{ background: 'linear-gradient(135deg, #3dffa0, #00d4b8)', color: '#0a0a0f' }}>
                      <FlaskConical size={13} /> Open Research Lab <ExternalLink size={11} />
                    </button>
                  </div>
                )}

                {/* Suggestions */}
                {resp.suggestions?.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mt-0.5">
                    {resp.suggestions.map((s, si) => (
                      <button key={si} onClick={() => sendMessage(s)} data-testid={`suggestion-${si}`}
                        className="text-[11px] px-2.5 py-1 rounded-full cursor-pointer transition-all"
                        style={{ background: 'rgba(0,212,184,0.08)', border: '1px solid rgba(0,212,184,0.2)', color: '#00d4b8' }}
                        onMouseEnter={e => e.currentTarget.style.background = 'rgba(0,212,184,0.15)'}
                        onMouseLeave={e => e.currentTarget.style.background = 'rgba(0,212,184,0.08)'}>
                        {s}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          );
        })}

        {/* Loading */}
        {loading && (
          <div className="flex gap-3 fade-in">
            <div className="w-8 h-8 rounded-lg flex-shrink-0 mt-0.5 flex items-center justify-center"
              style={{ background: 'linear-gradient(135deg, #7c5efa, #00d4b8)' }}>
              <Zap size={14} className="text-white" />
            </div>
            <div className="flex items-center gap-2.5 px-4 py-3 rounded-2xl text-sm"
              style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: '4px 16px 16px 16px' }}>
              <div className="w-3 h-3 rounded-full border-2 border-t-transparent animate-spin"
                style={{ borderColor: '#7c5efa', borderTopColor: 'transparent' }} />
              <span style={{ color: 'var(--llmc-text-muted)' }}>Einstein is thinking...</span>
            </div>
          </div>
        )}
        <div ref={endRef} />
      </div>

      {/* Input */}
      <div className="p-3 flex-shrink-0" style={{ background: 'rgba(10,10,20,0.95)', borderTop: '1px solid var(--llmc-border)' }}>
        {/* Attachment chips above input */}
        {attachments.length > 0 && (
          <div className="mb-2">
            <FileAttachments attachments={attachments} onChange={setAttachments} />
          </div>
        )}

        {isFirstUserMessage && (
          <div className="mb-2 px-3 py-2 rounded-xl flex items-center gap-2 text-[10px]"
            style={{ background: 'rgba(166,142,255,0.06)', border: '1px solid rgba(166,142,255,0.15)', color: 'rgba(166,142,255,0.7)' }}>
            <Brain size={10} />
            Einstein will conduct a smart discovery (up to 6 questions) before building your strategy
          </div>
        )}

        <div className="flex gap-2 items-end">
          <div className="flex-1 flex flex-col gap-1">
            <textarea
              className="flex-1 llmc-input px-3 py-2.5 text-sm resize-none w-full"
              rows={1}
              style={{ minHeight: 42, maxHeight: 120, overflowY: 'auto' }}
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); } }}
              placeholder="Tell Einstein what you want to achieve... (Enter to send)"
              data-testid="einstein-input"
              ref={inputRef}
            />
          </div>
          <FileAttachments attachments={attachments} onChange={setAttachments} disabled={loading} />
          {/* Voice controls */}
          <button onClick={() => startListening(t => setInput(t))}
            data-testid="strategist-mic-btn"
            title={isListening ? 'Listening...' : 'Speak your message'}
            className="w-10 h-10 flex-shrink-0 rounded-xl flex items-center justify-center transition-all"
            style={{ background: isListening ? 'rgba(255,80,80,0.2)' : 'rgba(255,255,255,0.06)', border: `1px solid ${isListening ? 'rgba(255,80,80,0.4)' : 'rgba(255,255,255,0.1)'}`, color: isListening ? '#ff5050' : 'rgba(255,255,255,0.4)' }}>
            {isListening ? <MicOff size={15} className="animate-pulse" /> : <Mic size={15} />}
          </button>
          <button onClick={() => setVoiceEnabled(v => !v)}
            data-testid="strategist-tts-btn"
            title={voiceEnabled ? 'Disable Einstein voice' : 'Enable Einstein voice (Kokoro TTS)'}
            className="w-10 h-10 flex-shrink-0 rounded-xl flex items-center justify-center transition-all"
            style={{ background: voiceEnabled ? 'rgba(61,255,160,0.12)' : 'rgba(255,255,255,0.06)', border: `1px solid ${voiceEnabled ? 'rgba(61,255,160,0.3)' : 'rgba(255,255,255,0.1)'}`, color: voiceEnabled ? '#3dffa0' : 'rgba(255,255,255,0.4)' }}>
            {isSpeaking ? <Volume2 size={15} className="animate-pulse" /> : voiceEnabled ? <Volume2 size={15} /> : <VolumeX size={15} />}
          </button>
          <button
            onClick={() => sendMessage()}
            disabled={!input.trim() || loading}
            data-testid="einstein-send-btn"
            className="w-10 h-10 flex-shrink-0 rounded-xl flex items-center justify-center llmc-btn-primary disabled:opacity-30"
          >
            <Send size={16} />
          </button>
        </div>
      </div>
    </div>
  );
}
